import React, { useEffect, useState, FormEvent, ChangeEvent, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, ListGroup, Alert, Modal, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useGame } from '../contexts/GameContext';
import { useAuth } from '../contexts/AuthContext';
import { GameSession, Player as GamePlayer } from '../types';

// Type augmentation to add properties to Player type
declare module '../types' {
  interface Player {
    displayName?: string;
    userName?: string;
  }
}
import { signalRService } from '../services/signalRService';
import gameService from '../services/gameService';
import toast from 'react-hot-toast';
import * as signalR from '@microsoft/signalr';

// Enhanced Player interface with connection status properties
// Using a custom interface to avoid collision with imported types
interface LobbyPlayer {
  id: string | number;
  userId: string;
  displayName: string; // Make sure it's always a string in this component
  playerName: string; // Make sure it's always a string in this component
  isHost: boolean;
  isConnected?: boolean;
  lastConnected?: Date;
  lastDisconnected?: Date;
  isActive?: boolean; // Property used in some functions
  connectionId?: string;
  // Add missing properties that might be coming from Player type
  score?: number;
  correctAnswers?: number;
  totalAnswers?: number;
}

// Interface for connected user info from SignalR
interface ConnectedUser {
  userId: string;
  userName?: string;
  connectionId: string;
  isConnected: boolean;
  lastConnected?: Date;
  lastDisconnected?: Date;
  isHost?: boolean; // Added to make type checking work
  id?: string | number; // Added to make type checking work
}

// Interface for user connection status from SignalR
interface UserConnectionStatus {
  userId: string;
  userName?: string;
  connectionId?: string;
  isConnected: boolean;
  timestamp?: Date;
}

export default function GameLobby() {
  const { gameSession, leaveGame, startGame, isInGame, isLoading, refreshGameSession } = useGame();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State variables
  const [showModal, setShowModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [copyStatus, setCopyStatus] = useState('Copy');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // State variables for invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  
  // Game space state
  const [isGameSpaceOwner, setIsGameSpaceOwner] = useState(false);
  const [becomingGuestHost, setBecomingGuestHost] = useState(false);
  const [gameStartError, setGameStartError] = useState<string | null>(null);
  
  // Player tracking state
  const [players, setPlayers] = useState<LobbyPlayer[]>([]);
  const [connectedUsers, setConnectedUsers] = useState<ConnectedUser[]>([]);
  const [previousPlayers, setPreviousPlayers] = useState<LobbyPlayer[]>([]);
  const [removingPlayerId, setRemovingPlayerId] = useState<string | number | null>(null);
  
  // State for tracking disconnection timeouts
  const [disconnectionTimers, setDisconnectionTimers] = useState<{[userId: string]: NodeJS.Timeout}>({});
  
  // Automatic disconnect timeout in milliseconds (5 minutes)
  const AUTO_DISCONNECT_TIMEOUT = 5 * 60 * 1000;

  // Enhanced debug logging on every render
  useEffect(() => {
    console.log('GameLobby render state:', {
      isInGame,
      isLoading,
      gameSessionId: gameSession?.id,
      gameSessionStatus: gameSession?.status,
      gameSessionStatusName: gameSession?.statusName,
      playerCount: gameSession?.players?.length || 0,
      userId: user?.id,
      renderTime: new Date().toISOString()
    });
  }, [isInGame, isLoading, gameSession, user]);

  // Determine if the current user is the host using player information from the game session
  const checkIsHost = () => {
    if (!user?.id || !gameSession) return false;
    
    console.log('[GameLobby] Checking host status with updated logic');
    console.log('[GameLobby] GameSession data:', { 
      hostId: (gameSession as any).hostId,
      sessionHostId: (gameSession as any).sessionHostId,
      hostUserId: (gameSession as any).hostUserId,
      sessionHostUserId: (gameSession as any).sessionHostUserId,
      currentUserId: user.id
    });
    
    // PRIMARY METHOD: Check if the current user's player entry is marked as host
    if (gameSession.players) {
      const currentUserPlayer = gameSession.players.find(p => p.userId == user.id);
      if (currentUserPlayer && currentUserPlayer.isHost) {
        console.log(`[GameLobby] Current user is explicitly marked as host in players list`);
        return true;
      }

      // Also check if any player is marked as host
      console.log('[GameLobby] Checking for host in players:', 
        gameSession.players.map(p => ({ userId: p.userId, isHost: p.isHost })));
      
      const hostPlayer = gameSession.players.find(p => p.isHost);
      if (hostPlayer) {
        const isCurrentUserHost = hostPlayer.userId == user.id;
        console.log(`[GameLobby] Found host player: ${hostPlayer.userId}, current user is host: ${isCurrentUserHost}`);
        if (isCurrentUserHost) {
          return true;
        }
      }
    }
    
    // FALLBACK 1: Check if host property matches current user
    if ('host' in gameSession) {
      const host = (gameSession as any).host;
      if (host && 'userId' in host && host.userId == user.id) {
        console.log('[GameLobby] Current user matched host.userId');
        return true;
      }
    }
    
    // FALLBACK 2: Don't check sessionHostId since user reported it's not the user.id
    
    // FALLBACK 3: Check hostUserId for older API versions
    if ('hostUserId' in gameSession) {
      const hostUserId = (gameSession as any).hostUserId;
      const isCurrentUserHost = hostUserId == user.id;
      console.log(`[GameLobby] Using hostUserId: ${hostUserId}, current user is host: ${isCurrentUserHost}`);
      if (isCurrentUserHost) {
        return true;
      }
    }
    
    console.log('[GameLobby] Could not determine host status, user is not host');
    return false;
  };
  
  // Host status is determined by the checkIsHost function
  const isHost = checkIsHost();
  
  // Log the host status for debugging
  console.log(`[GameLobby] Current user isHost: ${isHost}`);
  
  // Also ensure the host has isHost=true set for the current user in the game session
  useEffect(() => {
    if (user?.id && gameSession?.players && isHost) {
      console.log('[GameLobby] Ensuring host status is set in gameSession');
      const currentUserAsPlayer = gameSession.players.find(p => String(p.userId) === String(user.id));
      if (currentUserAsPlayer && !currentUserAsPlayer.isHost) {
        console.log('[GameLobby] Host status not correctly set for current user, fixing...');
        currentUserAsPlayer.isHost = true;
      }
    }
  }, [user?.id, gameSession, isHost]);

  // Handle connection state change
  useEffect(() => {
    if (!isInGame || isLoading) return;
    console.log(`[GameLobby] Connection state change - isHost: ${isHost}, gameSessionStatus: ${gameSession?.statusName}`);
  }, [isInGame, isLoading, isHost, gameSession?.statusName]);



  // Add a useEffect to ensure players list is updated whenever host status changes
  useEffect(() => {
    if (isHost && players.length > 0) {
      console.log('[GameLobby] Host status changed, updating players list');
      // Update the players list to ensure host is shown as connected
      setPlayers(prevPlayers => {
        return prevPlayers.map(player => {
          // If this player is the host, force them to be connected
          if (player.isHost || (user?.id && player.userId == user.id)) {
            return {
              ...player,
              isConnected: true,
              lastConnected: new Date()
            };
          }
          return player;
        });
      });
    }
  }, [isHost, user?.id]);

  // Handle removal of a disconnected player
  const handleRemoveDisconnectedPlayer = async (player: LobbyPlayer) => {
    // Check if the player is already being removed
    if (removingPlayerId === player.id) return;

    // Double check that current user is the host
    if (!isHost) {
      toast.error('Only the host can remove players');
      return;
    }

    // Confirmation dialog
    if (!window.confirm(`Remove ${player.displayName || player.playerName || 'this player'} from the game?`)) {
      return;
    }

    // Convert to string if it's a number to ensure type safety
    setRemovingPlayerId(String(player.id));

    try {
      // Call the backend API to remove the player
      await gameService.removePlayerFromGame(gameSession.id, player.userId);
      toast.success(`Player ${player.displayName || player.playerName} was removed from the game`);
      
      // Refresh the game session to update the player list
      await refreshGameSession();

      // Clear the disconnection timer for this player if it exists
      if (disconnectionTimers[player.userId]) {
        clearTimeout(disconnectionTimers[player.userId]);
        setDisconnectionTimers(prev => {
          const newTimers = {...prev};
          delete newTimers[player.userId];
          return newTimers;
        });
      }
    } catch (error) {
      console.error('Failed to remove player:', error);
      toast.error('Failed to remove player from the game');
    } finally {
      setRemovingPlayerId(null);
    }
  };

  // Join SignalR game group when component mounts (connection and handlers managed by GameContext)
  useEffect(() => {
    if (!gameSession?.id || !user?.id) return;

    const joinGameGroup = async () => {
      try {
        console.log(`[GameLobby] Joining game group ${gameSession.gameCode}...`);
        
        // Join the game group - connection should already be established by GameContext
        await signalRService.joinGameGroup(String(gameSession.gameCode));
        console.log(`[GameLobby] Joined game group ${gameSession.gameCode}`);
        
        // Request current connected users list
        await signalRService.requestConnectedUsers(String(gameSession.gameCode));
        console.log(`[GameLobby] Requested connected users for ${gameSession.gameCode}`);
      } catch (error) {
        console.error('[GameLobby] Error joining game group:', error);
      }
    };

    joinGameGroup();

    // Cleanup - leave game group when component unmounts
    return () => {
      try {
        if (gameSession?.gameCode) {
          signalRService.leaveGameGroup(String(gameSession.gameCode));
          console.log(`[GameLobby] Left game group ${gameSession.gameCode}`);
        }
      } catch (error) {
        console.error('[GameLobby] Error leaving game group:', error);
      }
    };
  }, [gameSession?.id, gameSession?.gameCode, user?.id]);

  // Effect to merge and update the displayPlayers list
  useEffect(() => {
    const apiPlayers = gameSession?.players || [];
    
    // Call the merge function to update the player list
    mergePlayerLists();
  }, [gameSession?.players, connectedUsers, user]);

  // Effect to set up SignalR event handlers
  useEffect(() => {
    if (gameSession?.id) {
      signalRService.onConnectedUsers((users: UserConnectionStatus[]) => {
        console.log('ConnectedUsers event received:', users);
        // Convert UserConnectionStatus[] to ConnectedUser[] for consistency
        const connectedUsers = users.map(u => ({
          userId: u.userId,
          userName: u.userName,
          connectionId: u.connectionId || '',
          isConnected: u.isConnected,
          lastConnected: u.timestamp || new Date()
        }));
        setConnectedUsers(connectedUsers);
      });

      // Register handler for GameStarted event
      signalRService.onGameStarted((data) => {
        console.log('GameStarted event received:', data);
        // Refresh game session to get latest game status
        refreshGameSession().catch(error =>
          console.error('Error refreshing game session after game started:', error)
        );
      });

      // Initial request for connected users
      if (gameSession?.id && gameSession?.gameCode) {
        try {
          console.log('Requesting latest connected users from SignalR...');
          if (signalRService.requestConnectedUsers) {
            signalRService.requestConnectedUsers(gameSession.gameCode).catch(error => {
              console.error('Error requesting connected users from SignalR:', error);
            });
          } else {
            console.error('signalRService.requestConnectedUsers is not defined');
          }
        } catch (signalRError) {
          console.error('Error requesting connected users from SignalR:', signalRError);
        }
      }

      return () => {
        // Clean up event handlers when component unmounts
        signalRService.removeAllListeners();
      };
    }
  }, [gameSession?.id, refreshGameSession]);
  
  // Merge player lists to show connection status
  const mergePlayerLists = useCallback(() => {
    if (!gameSession?.players) {
      console.log('[GameLobby] No game session or players to merge');
      return [];
    }

    const apiPlayers = gameSession.players;
    console.log('Merging player lists:', {
      apiPlayers: apiPlayers.length,
      connectedUsers: connectedUsers?.length || 0
    });

    // Merge API players with connection status from SignalR
    const mergedPlayers = apiPlayers.map((player: any) => {
      const userId = player.userId?.toString();
      const connectedUser = connectedUsers?.find(u => u.userId === userId);
      
      // Always mark the current user as connected if they are the host
      const isCurrentUserHost = user?.id?.toString() === userId && player.isHost;
      const isConnected = isCurrentUserHost || connectedUser?.isConnected || false;
      
      return {
        id: player.id || player.userId || '',
        userId: player.userId || '',
        displayName: player.displayName || player.playerName || player.userName || 'Unknown Player',
        playerName: player.playerName || player.userName || 'Unknown Player',
        isHost: player.isHost || false,
        isConnected: isConnected,
        lastConnected: connectedUser?.lastConnected,
        lastDisconnected: connectedUser?.lastDisconnected,
        connectionId: connectedUser?.connectionId,
        score: player.score,
        correctAnswers: player.correctAnswers,
        totalAnswers: player.totalAnswers
      };
    });

    console.log('[GameLobby] Merged players:', mergedPlayers);
    return mergedPlayers;
  }, [gameSession?.players, connectedUsers, user?.id]);

  // Effect to update players when data changes
  useEffect(() => {
    const merged = mergePlayerLists();
    setPlayers(merged);
  }, [mergePlayerLists]);

  // Effect to set up SignalR event handlers
  useEffect(() => {
    if (gameSession?.id) {
      signalRService.onConnectedUsers((users: UserConnectionStatus[]) => {
        console.log('ConnectedUsers event received:', users);
        // Convert UserConnectionStatus[] to ConnectedUser[] for consistency
        const connectedUsers = users.map(u => ({
          userId: u.userId,
          userName: u.userName,
          connectionId: u.connectionId || '',
          isConnected: u.isConnected,
          lastConnected: u.timestamp || new Date()
        }));
        setConnectedUsers(connectedUsers);
      });

      // Register handler for GameStarted event
      signalRService.onGameStarted((data) => {
        console.log('GameStarted event received:', data);
        // Refresh game session to get latest game status
        refreshGameSession().catch(error =>
          console.error('Error refreshing game session after game started:', error)
        );
      });

      // Initial request for connected users
      if (gameSession?.id && gameSession?.gameCode) {
        try {
          console.log('Requesting latest connected users from SignalR...');
          if (signalRService.requestConnectedUsers) {
            signalRService.requestConnectedUsers(gameSession.gameCode).catch(error => {
              console.error('Error requesting connected users from SignalR:', error);
            });
          } else {
            console.error('signalRService.requestConnectedUsers is not defined');
          }
        } catch (signalRError) {
          console.error('Error requesting connected users from SignalR:', signalRError);
        }
      }

      return () => {
        // Clean up event handlers when component unmounts
        signalRService.removeAllListeners();
      };
    }
  }, [gameSession?.id, refreshGameSession]);

  useEffect(() => {
    // Check if user is in a game
    if (!isInGame) {
      console.log('GameLobby: Not in a game, not navigating');
      // Don't navigate away, we'll show a message instead
      return;
    }

    console.log('GameLobby useEffect - Current game status:', {
      status: gameSession?.status,
      statusName: gameSession?.statusName
    });

    // If there's a current game but it's in progress, navigate to play
    if (gameSession?.statusName === 'InProgress') {
      console.log('Game is in progress, navigating to play page');
      // Use a short timeout to ensure the navigation happens after state is fully updated
      setTimeout(() => {
        console.log('Executing navigation to /play');
        navigate('/play');
      }, 100);
      return; // Exit early to avoid further processing
    }

    // Check if the user is the game space owner
    if (gameSession && user) {
  
      // This is a simplified check - in a real app, you'd make an API call to verify ownership
      // For now, we'll assume the host is the owner
      const isOwner = gameSession && gameSession.players ? gameSession.players.find((p: any) => p.userId === user.id.toString())?.isHost : false;
      setIsGameSpaceOwner(!!isOwner);
    }

    // Log player information for debugging
    console.log('Game session updated:', {
      sessionId: gameSession?.id,
      players: gameSession?.players?.map((p: any) => ({ id: p.id, name: p.displayName, isHost: p.isHost })),
      playerCount: gameSession?.players?.length || 0,
      hostUserId: (gameSession as any)?.hostUserId
    });
  }, [isInGame, gameSession, user, navigate]);

  // Functions to handle user interactions
  const handleRefreshPlayers = async () => {
    if (isRefreshing) return; // Prevent multiple refreshes

    setIsRefreshing(true);
    console.log('Refreshing game session and player list...');

    try {
      // Store current players before refresh to verify they're preserved
      const playersBefore = [...players]; // Create a copy of the current players
      const playerCountBefore = playersBefore.length || 0;
      console.log(`Players before refresh: ${playerCountBefore}`);

      // Save current players to previousPlayers state for fallback
      if (playersBefore.length > 0) {
        setPreviousPlayers(playersBefore);
      }

      // First refresh the game session from the API
      const success = await refreshGameSession();

      console.log('Requesting latest connected users from SignalR...');
      if (gameSession?.id && gameSession?.gameCode) {
        try {
          console.log('Requesting latest connected users from SignalR...');
          if (signalRService.requestConnectedUsers) {
            signalRService.requestConnectedUsers(gameSession.gameCode).catch(error => {
              console.error('Error requesting connected users from SignalR:', error);
            });
          } else {
            console.error('signalRService.requestConnectedUsers is not defined');
          }
        } catch (signalRError) {
          console.error('Error requesting connected users from SignalR:', signalRError);
        }
      } // After a short delay, check if the player list was cleared and restore it if needed
      setTimeout(() => {
        const playerCountAfter = players?.length || 0;
        console.log(`Players after refresh: ${playerCountAfter}`);

        if (playerCountAfter === 0 && playerCountBefore > 0) {
          console.error('Player list was cleared after refresh - forcing manual restore');

          // First try to use API players if available
          if (gameSession?.players && gameSession.players.length > 0) {
            console.log('Restoring player list from API data...');

            const apiPlayers = gameSession.players || [];

            // Create a map to store merged players
            const playerMap = new Map<string, LobbyPlayer>();

            // Add API players with connection status from connectedUsers
            apiPlayers.forEach(player => {
              // Determine if this player is connected using connectedUsers data
              const connectedUser = connectedUsers.find(cu => cu.userId == player.userId);
              const isCurrentUser = user?.id && player.userId == user.id;
              
              // Determine connection status based on connectedUsers or current user status
              let isConnected = false;
              if (isCurrentUser) {
                // The current user (host) is always connected
                isConnected = true;
              } else if (connectedUser) {
                // Use connection status from connectedUsers
                isConnected = connectedUser.isConnected;
              } else if ('isConnected' in player) {
                // Use connection status from player
                isConnected = (player as any).isConnected;
              } else if ('isActive' in player) {
                // Use connection status from player
                isConnected = (player as any).isActive;
              } 
              
              console.log(`[GameLobby] Refreshing player ${player.playerName} (${player.userId}), connected: ${isConnected}`);
              
              // Create properly typed LobbyPlayer object to avoid TypeScript errors
              const lobbyPlayer: LobbyPlayer = {
                id: player.id || player.userId || '', // Ensure id is never undefined
                userId: player.userId || '',
                displayName: player.displayName || player.playerName || (player as any).userName || 'Unknown Player',
                playerName: player.playerName || (player as any).userName || 'Unknown Player',
                isHost: player.isHost || false,
                isConnected: isConnected, // Use the determined connection status
                connectionId: connectedUser?.connectionId,
                lastConnected: connectedUser?.lastConnected,
                lastDisconnected: connectedUser?.lastDisconnected,
                score: (player as any)?.score,
                correctAnswers: (player as any)?.correctAnswers,
                totalAnswers: (player as any)?.totalAnswers
              };
              
              playerMap.set(player.userId, lobbyPlayer);
            });

            // Convert map back to array and update display
            const mergedPlayers = Array.from(playerMap.values());
            setPlayers(mergedPlayers);

            console.log('Player list restored from API with', mergedPlayers.length, 'players');
          }
          // If no API players, use previously saved players as fallback
          else if (previousPlayers.length > 0) {
            console.log('Restoring player list from previous players...');
            setPlayers(previousPlayers);
            console.log('Player list restored from previous players with', previousPlayers.length, 'players');
          }
        }
      }, 1500); // Check after 1.5 seconds to allow state updates to complete
      
      if (success) {
        console.log('Game session refreshed successfully');
        
        // After successful refresh, we need to merge the player list with connectedUsers
        // to preserve connection status information which isn't in the API response
        setTimeout(() => {
          if (gameSession && gameSession.players) {
            console.log('[GameLobby] Merging player lists after successful refresh to preserve connection statuses');
            setPlayers(mergePlayerLists());
          }
        }, 300); // Short delay to ensure game session state is updated
      } else {
        console.error('Failed to refresh game session');
      }

    } catch (error) {
      console.error('Error refreshing game session:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLeaveGame = async () => {
    if (window.confirm('Are you sure you want to leave this game?')) {
      await leaveGame();
      navigate('/');
    }
  };

  const handleCopyGameCode = () => {
    if (gameSession?.gameCode) {
      navigator.clipboard.writeText(gameSession.gameCode)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 3000);
        })
        .catch(error => {
          console.error('Error copying game code:', error);
        });
    }
  };

  // Function to handle becoming a guest host
  const handleBecomeGuestHost = async () => {
    if (!gameSession?.id) return;

    try {
      setBecomingGuestHost(true);

      // Call the API to become a guest host using our gameService
      const response = await gameService.becomeGuestHost(gameSession.id);

      if (response.success) {
        console.log('Successfully became guest host:', response.data);
        // Refresh game session to update host status
        await refreshGameSession();
      } else {
        console.error('Failed to become guest host:', response.message);
        alert('Failed to become guest host: ' + (response.message || 'Unknown error'));
      }
    } catch (error: any) {
      console.error('Error becoming guest host:', error);
      alert('An error occurred while becoming guest host: ' + (error?.message || 'Unknown error'));
    } finally {
      setBecomingGuestHost(false);
    }
  };

  // Handle sending invites
  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setIsInviting(true);
    try {
      // In a real app, you'd call an API to send the invite
      // For now, we'll just simulate it with a timeout
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Reset form
      setInviteEmail('');
      setInviteMessage('');
      setShowInviteModal(false);

      // Show success message (you could add a toast notification here)
      alert('Invitation sent successfully!');
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('Failed to send invitation. Please try again.');
    } finally {
      setIsInviting(false);
    }
  };
  
  // Show loading state
  if (isLoading) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <div className="spinner-border text-primary" />
            <p className="mt-2 text-muted">Loading game...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  // Show message when there's no active game
  if (!isInGame || !gameSession) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="border-0 shadow">
              <Card.Body className="p-4 text-center">
                <i className="fas fa-exclamation-circle fa-3x text-warning mb-3"></i>
                <h3>No Active Game</h3>
                <p className="text-muted mb-4">
                  There is no active game session. You can join an existing game or create a new one.
                </p>
                <div className="d-grid gap-3">
                  <Button onClick={() => navigate('/join')} variant="primary">
                    <i className="fas fa-sign-in-alt me-2"></i>
                    Join a Game
                  </Button>
                  <Button onClick={() => navigate('/create')} variant="outline-primary">
                    <i className="fas fa-plus me-2"></i>
                    Create New Game
                  </Button>
                  <Button onClick={() => navigate('/')} variant="link" className="text-muted">
                    <i className="fas fa-home me-2"></i>
                    Back to Home
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Render the invite modal inside the main component
  const renderInviteModal = () => (
    <InvitePlayersModal
      show={showInviteModal}
      onHide={() => setShowInviteModal(false)}
      currentGame={gameSession}
      inviteEmail={inviteEmail}
      setInviteEmail={setInviteEmail}
      inviteMessage={inviteMessage}
      setInviteMessage={setInviteMessage}
      handleSendInvite={handleSendInvite}
      isInviting={isInviting}
      handleCopyGameCode={handleCopyGameCode}
      copySuccess={copySuccess}
    />
  );

  return (
    <>
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col lg={8}>
            {/* Game Header */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col>
                    <h2 className="mb-1">{gameSession?.title || 'Game Lobby'}</h2>
                    {gameSession?.description && (
                      <p className="text-muted mb-2">{gameSession.description}</p>
                    )}
                    <div className="d-flex align-items-center gap-3">
                      <Badge bg="success" className="px-3 py-2">
                        <i className="fas fa-clock me-1"></i>
                        Waiting for Players
                      </Badge>
                      <span className="text-muted">
                        <i className="fas fa-book me-1"></i>
                        {gameSession?.questionCollectionTitle || 'Question Collection'}
                      </span>
                    </div>
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="outline-danger"
                      onClick={handleLeaveGame}
                      className="me-2"
                    >
                      <i className="fas fa-sign-out-alt me-1"></i>
                      Leave
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Player List */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    <i className="fas fa-users me-2 text-primary"></i>
                    Players ({players?.length || 0})
                  </h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleRefreshPlayers}
                    disabled={isRefreshing}
                  >
                    {isRefreshing ? (
                      <>
                        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                        Refreshing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sync-alt me-1"></i>
                        Refresh
                      </>
                    )}
                  </Button>
                </div>

                {/* Players list */}
                <ListGroup variant="flush" className="mt-3">
                  {players.length === 0 ? (
                    <ListGroup.Item className="text-center text-muted">
                      <i className="fas fa-info-circle me-2"></i>
                      No players have joined yet
                    </ListGroup.Item>
                  ) : (
                    players.map((player, index) => (
                      <ListGroup.Item
                        key={player.id || `player-${index}`}
                        className="d-flex justify-content-between align-items-center px-0"
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                            style={{ width: '40px', height: '40px', fontSize: '14px', fontWeight: 'bold' }}
                          >
                            {player.playerName ? player.playerName.substring(0, 2).toUpperCase() : 'P?'}
                          </div>
                          <div>
                            <div className="fw-semibold">{player.playerName || 'Unknown Player'}</div>
                            {player.isHost && (
                              <small className="text-muted">
                                <i className="fas fa-crown me-1 text-warning"></i>
                                Game Host
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="d-flex align-items-center">
                          {player.isHost && (
                            <Badge bg="warning" text="dark" className="me-2">Host</Badge>
                          )}
                          {/* Force host to always show as connected */}
                          <span className={`badge ${(player.isHost || player.isConnected) ? 'bg-success' : 'bg-danger'} d-flex align-items-center`}>
                            {(player.isHost || player.isConnected) ? (
                              <>
                                <span className="spinner-grow spinner-grow-sm me-1" style={{ width: '6px', height: '6px' }}></span>
                                Connected
                              </>
                            ) : (
                              <>
                                <i className="fas fa-times-circle me-1"></i>
                                Disconnected
                                {player.lastDisconnected && (
                                  <span className="ms-1 small">
                                    {Math.floor((Date.now() - new Date(player.lastDisconnected).getTime()) / 60000)}m ago
                                  </span>
                                )}
                              </>
                            )}
                          </span>
                          {isHost && !player.isHost && !player.isConnected && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="ms-2"
                              onClick={() => handleRemoveDisconnectedPlayer(player)}
                            >
                              <i className="fas fa-user-slash"></i>
                            </Button>
                          )}
                        </div>
                      </ListGroup.Item>
                    ))
                  )}
                </ListGroup>

                {gameSession?.players && gameSession?.maxPlayers && gameSession.players.length < gameSession.maxPlayers && (
                  <div className="text-center mt-3 p-3 border border-dashed rounded">
                    <i className="fas fa-user-plus fa-2x text-muted mb-2"></i>
                    <p className="text-muted mb-0">
                      {(() => {
                        const playersNeeded = (gameSession?.maxPlayers || 0) - (gameSession?.players?.length || 0);
                        return `Waiting for ${playersNeeded} more player${playersNeeded !== 1 ? 's' : ''}`;
                      })()}
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            {/* Game Info & Actions */}
            <Card className="border-0 shadow-sm mb-3">
              <Card.Header className="bg-primary text-white">
                <h6 className="mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  Game Info
                </h6>
              </Card.Header>
              <Card.Body>
                    <div className="mb-3">
                      <label className="small text-muted">Game Code</label>
                      <div className="d-flex align-items-center">
                        <code className="bg-light px-2 py-1 rounded me-2 flex-grow-1">
                          {gameSession?.gameCode || 'N/A'}
                        </code>
                        <Button 
                          size="sm" 
                          variant="outline-primary"
                          onClick={handleCopyGameCode}
                        >
                          {copySuccess ? (
                            <i className="fas fa-check"></i>
                          ) : (
                            <i className="fas fa-copy"></i>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Host Information */}
                    <div className="mb-3">
                      <label className="small text-muted">Game Host</label>
                      <div className="d-flex align-items-center">
                        <div className="bg-light px-2 py-1 rounded w-100">
                          {(() => {
                            // Find host player from the players list
                            const hostPlayer = gameSession.hostName;
                            return hostPlayer ? (
                              <div className="d-flex align-items-center">
                                <div 
                                  className="rounded-circle bg-warning text-dark d-flex align-items-center justify-content-center me-2"
                                  style={{ width: '24px', height: '24px', fontSize: '12px', fontWeight: 'bold' }}
                                >
                                  {hostPlayer ? hostPlayer.substring(0, 2).toUpperCase() : 'H?'}
                                </div>
                                <span>{hostPlayer || 'Unknown Host'}</span>
                              </div>
                            ) : (
                              <span className="text-muted">Broken</span>
                            );
                          })()}
                    </div>
                  </div>
                </div>

                {/* Host information is already displayed above */}

                <div className="mb-3">
                  <label className="small text-muted">Max Players</label>
                  <div>{gameSession?.maxPlayers || 0}</div>
                </div>

                <div className="mb-0">
                  <label className="small text-muted">Game Type</label>
                  <div>
                    {gameSession?.isPublic ? (
                      <span className="text-success">
                        <i className="fas fa-globe me-1"></i>
                        Public
                      </span>
                    ) : (
                      <span className="text-warning">
                        <i className="fas fa-lock me-1"></i>
                        Private
                      </span>
                    )}
                  </div>
                </div>
                  </Card.Body>
              </Card>

          {/* Host Controls */}
          {isHost && (
            <Card className="border-warning">
              <Card.Header className="bg-warning text-dark">
                <h6 className="mb-0">
                  <i className="fas fa-crown me-2"></i>
                  Host Controls
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button 
                    variant="success"
                    disabled={!gameSession?.players || isLoading}
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to start the game? All players will be moved to the game screen.')) {
                        await startGame();
                      }
                    }}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Starting Game...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-play me-2"></i>
                        Start Game
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="info"
                    onClick={() => setShowInviteModal(true)}
                  >
                    <i className="fas fa-user-plus me-2"></i>
                    Invite Players
                  </Button>
                  <Button variant="outline-secondary">
                    <i className="fas fa-cog me-2"></i>
                    Game Settings
                  </Button>
                </div>
                
                {gameSession?.players && gameSession.players.length < 2 && (
                  <Alert variant="info" className="mt-3 mb-0 small">
                    <i className="fas fa-info-circle me-1"></i>
                    At least 2 players needed to start
                  </Alert>
                )}
              </Card.Body>
            </Card>
          )}
          
          {/* Game Space Owner Controls (if not already host) */}
          {isGameSpaceOwner && !isHost && (
            <Card className="border-primary mt-3">
              <Card.Header className="bg-primary text-white">
                <h6 className="mb-0">
                  <i className="fas fa-user-shield me-2"></i>
                  Game Space Owner Controls
                </h6>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button 
                    variant="info"
                    onClick={() => setShowInviteModal(true)}
                  >
                    <i className="fas fa-user-plus me-2"></i>
                    Invite Players
                  </Button>
                  <Button variant="outline-secondary">
                    <i className="fas fa-cog me-2"></i>
                    Game Space Settings
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Waiting Message for Non-Hosts */}
          {!isHost && (
            <Card className="border-info">
              <Card.Body className="text-center">
                <i className="fas fa-hourglass-half fa-2x text-info mb-3"></i>
                <h6>Waiting for Host</h6>
                <p className="text-muted small mb-0">
                  The game will start when the host is ready
                </p>
                
                {/* Guest Host Offer when allowExternalHost is true */}
                {gameSession?.allowExternalHost && (
                  <div className="mt-3">
                    <hr />
                    <h6 className="text-primary">
                      <i className="fas fa-crown me-2"></i>
                      Want to start the game yourself?
                    </h6>
                    <p className="small text-muted">
                      This game space allows guest hosting.
                    </p>
                    <Button 
                      variant="outline-primary" 
                      className="mt-1"
                      onClick={handleBecomeGuestHost}
                      disabled={becomingGuestHost}
                    >
                      {becomingGuestHost ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Becoming Guest Host...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-user-check me-2"></i>
                          Become Guest Host
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Col>
      </Row>
      {renderInviteModal()}
    </Container>
    </>
  );
}

// Invite Players Modal Component
interface InvitePlayersModalProps {
  show: boolean;
  onHide: () => void;
  currentGame: GameSession | null;
  inviteEmail: string;
  setInviteEmail: (email: string) => void;
  inviteMessage: string;
  setInviteMessage: (message: string) => void;
  handleSendInvite: (e: React.FormEvent) => void;
  isInviting: boolean;
  handleCopyGameCode: () => void;
  copySuccess: boolean;
}

function InvitePlayersModal({
  show,
  onHide,
  currentGame,
  inviteEmail,
  setInviteEmail,
  inviteMessage,
  setInviteMessage,
  handleSendInvite,
  isInviting,
  handleCopyGameCode,
  copySuccess
}: InvitePlayersModalProps) {
  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-user-plus me-2 text-primary"></i>
          Invite Players
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSendInvite}>
          <Form.Group className="mb-3">
            <Form.Label>Email Address</Form.Label>
            <Form.Control 
              type="email" 
              placeholder="Enter email address"
              value={inviteEmail}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setInviteEmail(e.target.value)}
              required
            />
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Personal Message (Optional)</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3}
              placeholder="Add a personal message..."
              value={inviteMessage}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setInviteMessage(e.target.value)}
            />
          </Form.Group>
          
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-1">Game Code</h6>
              <code className="fs-5">{currentGame?.gameCode}</code>
            </div>
            <Button 
              variant="primary" 
              type="submit"
              disabled={isInviting || !inviteEmail.trim()}
            >
              {isInviting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Sending...
                </>
              ) : (
                <>Send Invite</>
              )}
            </Button>
          </div>
        </Form>
        
        <hr />
        
        <div className="mt-3">
          <h6>Share Game Code</h6>
          <p className="small text-muted mb-2">Players can join using this code:</p>
          <div className="d-flex">
            <input 
              type="text" 
              className="form-control" 
              value={currentGame?.gameCode || ''} 
              readOnly 
            />
            <Button 
              variant="outline-secondary" 
              className="ms-2"
              onClick={handleCopyGameCode}
            >
              <i className="fas fa-copy"></i>
            </Button>
          </div>
          {copySuccess && <small className="text-success mt-1 d-block">Copied!</small>}
        </div>
      </Modal.Body>
    </Modal>
  );
}
